import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ICreateExpenseData, ICreateInvoiceData, ICustomer } from '../../../@types';
import ExpandRight from '../../../assets/icons/ExpandRight';
import {
  validateExpenseBasicInfo,
  validateExpenseItemDetails,
  validateExpenseTotalPayment,
} from '../../../modules/expense/expense';
import {
  InvoiceErrors,
  validateInvoiceBasicInfo,
  validateInvoiceItemDetails,
  validateInvoiceSummary,
} from '../../../modules/validateInvoiceForm';
import { Button } from '../../common';
import ProgressBar from './ProgressBar';

export interface StepFormProps {
  stepTitles: string[];
  children: ReactNode;
  nextButtonHandler: () => void;
  backButtonHandler: () => void;
  cancelButtonHandler: () => void;
  commonButtonText?: string;
  handleConfirmCreateExpense?: (event: any) => Promise<void>;
  onSaveDraft?: (event: any) => Promise<void>;
  handleCancel?: () => void;
  formData: ICreateExpenseData | ICreateInvoiceData;
  onCreateInvoice?: (event: any) => Promise<void>;
  step1Validated?: boolean;
  handleSetCustomer?: (selectedCustomer: ICustomer | null) => void;
  validationResults: InvoiceErrors;
  setValidationResults: React.Dispatch<React.SetStateAction<InvoiceErrors>>;
  isLoading?: boolean;
  isDraftLoading?: boolean;
}

const StepForm: FC<StepFormProps> = ({
  children,
  stepTitles,
  nextButtonHandler,
  backButtonHandler,
  cancelButtonHandler,
  commonButtonText,
  handleConfirmCreateExpense,
  onSaveDraft,
  handleCancel,
  formData,
  onCreateInvoice,
  step1Validated,
  handleSetCustomer,
  validationResults,
  setValidationResults,
  isLoading,
  isDraftLoading,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const { invoiceNo } = useParams<{ invoiceNo: string }>();
  const totalSteps = React.Children.count(children);
  const [, setStep1Validated] = useState(false);
  const [stepValidationResults, setStepValidationResults] = React.useState<InvoiceErrors[]>(Array(totalSteps).fill({}));
  const [stepTitlesTranslateX, setStepTitlesTranslateX] = useState(0);

  useEffect(() => {
    const stepTitleWidth = 150;
    const newTranslateX = -activeStep * stepTitleWidth;
    setStepTitlesTranslateX(newTranslateX);
  }, [activeStep]);

  const validateCurrentStep = (): boolean => {
    let isValid = true;

    if ('isExpense' in formData && formData.isExpense) {
      const expenseValidationFunctionMap = [
        validateExpenseBasicInfo,
        validateExpenseItemDetails,
        validateExpenseTotalPayment,
      ];

      const currentValidationFunction = expenseValidationFunctionMap[activeStep];

      if (currentValidationFunction) {
        //@ts-ignore
        isValid = currentValidationFunction(formData, setValidationResults);
      }
    } else {
      const invoiceValidationFunctionMap = [
        validateInvoiceBasicInfo,
        validateInvoiceItemDetails,
        validateInvoiceSummary,
      ];

      const currentValidationFunction = invoiceValidationFunctionMap[activeStep];

      if (currentValidationFunction) {
        isValid = currentValidationFunction(formData, setValidationResults);
      }
    }

    return isValid;
  };

  const updateStepValidationResults = (stepIndex: number, results: InvoiceErrors) => {
    const updatedResults = [...stepValidationResults];
    updatedResults[stepIndex] = results;
    setStepValidationResults(updatedResults);
  };

  const handleNext = () => {
    const isValid = validateCurrentStep();

    if (isValid) {
      if (activeStep === 0) {
        setStep1Validated(true);
      }
      updateStepValidationResults(activeStep, {});
      nextButtonHandler();
      if (activeStep < totalSteps - 1) setActiveStep(activeStep + 1);
    } else {
      updateStepValidationResults(activeStep, validationResults);
    }
  };

  const handleBack = () => {
    backButtonHandler();
    if (activeStep > 0) setActiveStep(activeStep - 1);
  };

  return (
    <div>
      <div
        className="flex w-full  ml-[0.1em] mt-[30px] space-x-[6em] 540:ml-[0.1em] 540:space-x-[10em] bigmd:space-x-[11.5em] md:space-x-[10em] md:ml-[8em] bigmd:ml-[10.5em] lg:space-x-[10em] lg:ml-[6.5em] biglg:space-x-[10em] biglg:ml-[14.5em] xl:space-x-[9.5em] xl:ml-[19.5em] 2xl:space-x-[9.5em] 2xl:ml-[24.5em] 3xl:space-x-[9.5em] 3xl:ml-[31.5em] 4xl:space-x-[9.5em] 4xl:ml-[42.5em]"
        style={{ whiteSpace: 'nowrap' }}
      >
        {stepTitles.map((title, index) => (
          <div key={index} className={`lg:text-lg text-md ${index === activeStep ? 'text-black' : 'text-gray-500'}`}>
            {index === activeStep && title}
          </div>
        ))}
      </div>
      <ProgressBar steps={totalSteps} activeStep={activeStep} setActiveStep={setActiveStep} />
      {React.Children.map(children, (child, index) => {
        if (index !== activeStep) return null;
        return child;
      })}

       <hr className="hidden lg:block border-t h-[26px] border-[#C0C0C0] lg:mt-[259px] md:mt-[259px]"/> 

      <div className="flex lg:justify-end md:justify-end  justify-center gap-[1em] items-center mt-[3em] p-2  ">
        {activeStep > 0 && activeStep !== totalSteps - 1 && (
          <button
            className="bg-[#ffffff] h-[40px] w-[112px] gap-4 rounded-sm border border-[#044E97] mt-[2.5em] 375:mt-[0.8em] 360:mt-[0.8em] ml-[-2em] md:ml-[14em] md:mt-[1em] lg:ml-[13em] bigmd:ml-[22em] 540:ml-[6em] biglg:ml-[29em] lg:mt-[-4em] md:w-[215px] xl:ml-[39em] xl:mt-[-3.8em] 2xl:ml-[49em] 2xl:mt-[-4em]"
            onClick={handleBack}
          >
            Back
          </button>
        )}
        {activeStep === 0 && (
          <button
            className="bg-[#ffffff] text-[16px] h-[40px] w-[112px] 540:ml-[6em] gap-4 border border-[#044E97] rounded-sm mt-[2.5em]  md:mt-[3em] lg:mt-[-2em] xl:mt-[-1.8em] md:ml-[13em] md:w-[215px] lg:ml-[26em] bigmd:ml-[24em] xl:ml-[37em] 2xl:ml-[47em] xl:w-[215px] mb-[2em]"
            onClick={() => {
              cancelButtonHandler();
              navigate('/invoices');
            }}
          >
            Cancel
          </button>
        )}
        {activeStep < totalSteps - 1 && (
          <button
            className="bg-[#044E97] text-[16px]  h-[40px] 540:mr-[5em] w-[112px]  rounded-sm text-[#ffffff] ml:[2em] md:mr-[-1.5em] mt-[2.7em] md:mt-[2.9em] xl:mt-[-1.8em] md:w-[215px] lg:mr-[-0.5em]  lg:mt-[-2em] xl:w-[215px] xl:mr-[-0.5em] mb-[2em]"
            onClick={handleNext}
          >
            Next
          </button>
        )}
        {activeStep === totalSteps - 1 && (
          <>
            <button
            className=" hidden md:block biglg:mr-[-32em] text-[16px] lg:mt-[-2.5em] xl:mr-[-43em] 2xl:mr-[-49em]  lg:mr-[-17em] bigmd:mr-[-23em] md:mt-[1.5em] md:mr-[-17em]"
            onClick={handleBack}
          >
            Back
          </button>
            {onSaveDraft && (
              <div className=" hidden md:block ml-[em]   md:ml-[18em] lg:ml-[17.7em] mt-[1em] lg:mt-[-2.5em] w-[167px] lg:w-[215px] biglg:w-[215px] xl:w-[215px] md:w-[215px] bigmd:ml-[24em] biglg:ml-[33em] xl:ml-[44em] 2xl:ml-[52em] 540:ml-[4em] 360:ml-[-3em] p-2">
                <Button
                  text="Save Draft"
                  size="medium"
                  outline
                  onClick={onSaveDraft}
                  isLoading={isDraftLoading}
                  disabled={isDraftLoading}
                />
              </div>
            )}
            {handleCancel && (
              <div className=" hidden md:block ml-[em]   md:ml-[18em] lg:ml-[17.7em] mt-[1em] lg:mt-[-2.5em] w-[167px] lg:w-[215px] biglg:w-[215px] xl:w-[215px] md:w-[215px] bigmd:ml-[24em] biglg:ml-[33em] xl:ml-[44em] 2xl:ml-[52em] 540:ml-[4em] 360:ml-[-3em] p-2">
                <Button text="Cancel" size="medium" onClick={handleCancel} type="button" variant="danger" outline />
              </div>
            )}
            {onCreateInvoice && (
              <div className="hidden md:block  w-[167px] mt-[1em] lg:mt-[-2.5em]  md:mr-[6em] mr-[-3em] p-2 540:mr-[4em]  ">
                <Button
                  text={commonButtonText}
                  size="medium"
                  type="submit"
                  isLoading={isLoading}
                  disabled={isLoading}
                  onClick={onCreateInvoice}
                />
              </div>
            )}
            {handleConfirmCreateExpense && (
              <div className="hidden md:block w-[167px] mt-[1em] lg:mt-[-2.5em]  md:mr-[6em] mr-[-3em] p-2 540:mr-[4em] ">
                <Button
                  text="Save"
                  size="medium"
                  type="submit"
                  isLoading={isLoading}
                  disabled={isLoading}
                  onClick={handleConfirmCreateExpense}
                />
              </div>
            )}
     
    {onSaveDraft && (
      <div className="flex md:hidden  ml-[em]   md:ml-[18em] lg:ml-[17.7em] mt-[1em] lg:mt-[-2.5em] w-[167px] lg:w-[215px] biglg:w-[215px] xl:w-[215px] md:w-[215px] bigmd:ml-[24em] biglg:ml-[33em] xl:ml-[44em] 2xl:ml-[52em] 540:ml-[4em] 360:ml-[-3em] p-2">
        <Button
          text="Save Draft"
          size="small"
          outline
          onClick={onSaveDraft}
          isLoading={isDraftLoading}
          disabled={isDraftLoading}
        />
      </div>
    )}
    {handleCancel && (
      <div className=" flex md:hidden   ml-[em]   md:ml-[18em] lg:ml-[17.7em] mt-[1em] lg:mt-[-2.5em] w-[167px] lg:w-[215px] biglg:w-[215px] xl:w-[215px] md:w-[215px] bigmd:ml-[24em] biglg:ml-[33em] xl:ml-[44em] 2xl:ml-[52em] 540:ml-[4em] 360:ml-[-3em] p-2">
        <Button text="Cancel" size="small" onClick={handleCancel} type="button" variant="danger" outline />
      </div>
    )}
    {onCreateInvoice && (
      <div className="flex md:hidden  w-[167px] mt-[1em] lg:mt-[-2.5em]  md:mr-[6em] mr-[-3em] p-2 540:mr-[4em]  ">
        <Button
          text={commonButtonText}
          size="small"
          type="submit"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={onCreateInvoice}
        />
      </div>
    )}
    {handleConfirmCreateExpense && (
      <div className="flex md:hidden w-[167px] mt-[1em] lg:mt-[-2.5em]  md:mr-[6em] mr-[-3em] p-2 540:mr-[4em] ">
        <Button
          text="Save"
          size="small"
          type="submit"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={handleConfirmCreateExpense}
        />
      </div>
    )}
  </>

        )}
      </div>
      </div>
  );
};

export default StepForm;
